import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const SERVICES = ["SBU", "SKK Konstruksi", "Sertifikat Standar", "NIB / OSS RBA", "ISO / K3", "Lainnya"];
const svcLabel = (s, t) => (s === "Lainnya" ? t("lead.other") : s);
const EMPTY = { name: "", company: "", email: "", phone: "", service: "SBU", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-slate-300">{label}</span>
    {children}
  </label>
);
const cls = "w-full border border-white/15 bg-white/5 px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-[#F59E0B]";

export default function LeadForm({ profile }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const { t } = useSite();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", f);
      toast.success(t("lead.success"));
      setF(EMPTY);
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section id="konsultasi" className="bg-[#0F2B48] py-16 text-white sm:py-24" data-testid="lead-form-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-5">
          <p className="eyebrow mb-4">{t("lead.eyebrow")}</p>
          <h2 className="text-3xl font-bold tracking-tight lg:text-4xl">{t("lead.title")}</h2>
          <p className="mt-5 text-slate-300">{t("lead.desc")}</p>
          <dl className="mt-10 space-y-4 text-sm">
            {[[t("lead.address"), profile?.address], [t("lead.email"), profile?.email], [t("lead.phone"), profile?.phone], [t("lead.hours"), profile?.hours]].map(([k, v]) => (
              <div key={k} className="border-l-2 border-[#D97706] pl-4"><dt className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{k}</dt><dd className="mt-0.5">{v}</dd></div>
            ))}
          </dl>
        </div>
        <form onSubmit={submit} className="grid gap-5 border border-white/10 bg-[#0B132B]/60 p-8 sm:grid-cols-2 lg:col-span-7" data-testid="lead-form">
          <Field label={t("lead.name")}><input required minLength={2} value={f.name} onChange={set("name")} className={cls} data-testid="lead-name-input" /></Field>
          <Field label={t("lead.company")}><input value={f.company} onChange={set("company")} className={cls} data-testid="lead-company-input" /></Field>
          <Field label={t("lead.wa")}><input required minLength={6} value={f.phone} onChange={set("phone")} className={cls} placeholder="08xxxxxxxxxx" data-testid="lead-phone-input" /></Field>
          <Field label={t("lead.email")}><input type="email" value={f.email} onChange={set("email")} className={cls} data-testid="lead-email-input" /></Field>
          <div className="sm:col-span-2">
            <Field label={t("lead.service")}>
              <div className="flex flex-wrap gap-2">
                {SERVICES.map((s) => (
                  <button type="button" key={s} onClick={() => setF({ ...f, service: s })} data-testid={`lead-service-${s.toLowerCase().replace(/\W+/g, "-")}`}
                    className={`border px-3 py-2 text-xs transition-colors ${f.service === s ? "border-[#D97706] bg-[#D97706]" : "border-white/20 hover:border-white/50"}`}>{svcLabel(s, t)}</button>
                ))}
              </div>
            </Field>
          </div>
          <div className="sm:col-span-2"><Field label={t("lead.message")}><textarea rows={4} value={f.message} onChange={set("message")} className={cls} data-testid="lead-message-input" /></Field></div>
          <button disabled={busy} data-testid="lead-submit-button" className="inline-flex items-center justify-center gap-2 bg-[#D97706] px-6 py-4 text-sm font-bold transition-colors hover:bg-[#B45309] disabled:opacity-60 sm:col-span-2">
            <Send size={16} /> {busy ? t("lead.sending") : t("lead.submit")}
          </button>
        </form>
      </div>
    </section>
  );
}
